import {
  NOTIFICATION_COUNT_LIMIT,
  NotificationFilterModeEnum,
  NotificationNavigationKindEnum,
  type NotificationFilterMode,
  type NotificationGroup,
  type NotificationNavigationTarget,
  type Notification,
} from './notifications.types';

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

export function formatNotificationCount(
  count: number,
  hasMoreEntries: boolean,
  limit = NOTIFICATION_COUNT_LIMIT
): string {
  const normalizedCount = Math.max(0, Math.floor(count));
  if (normalizedCount > limit || (hasMoreEntries && normalizedCount >= limit)) {
    return `${limit}+`;
  }

  return normalizedCount.toString();
}

export function isMessageNotification(notification: Notification): boolean {
  return !notification.entry.link;
}

export function resolveNotificationNavigation(
  link: string | undefined,
  currentOrigin: string
): NotificationNavigationTarget {
  if (link !== undefined && typeof link !== 'string') {
    return { kind: NotificationNavigationKindEnum.Invalid };
  }

  const trimmedLink = link?.trim();
  if (!trimmedLink) {
    return { kind: NotificationNavigationKindEnum.None };
  }

  try {
    const currentUrl = new URL(currentOrigin);
    const targetUrl = new URL(trimmedLink, currentUrl);
    if (targetUrl.protocol !== 'http:' && targetUrl.protocol !== 'https:') {
      return { kind: NotificationNavigationKindEnum.Invalid };
    }

    if (targetUrl.origin === currentUrl.origin) {
      return {
        kind: NotificationNavigationKindEnum.Internal,
        url: `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`,
      };
    }

    return { kind: NotificationNavigationKindEnum.External, url: targetUrl.href };
  } catch {
    return { kind: NotificationNavigationKindEnum.Invalid };
  }
}

export function resolveNotificationHref(
  link: string | undefined,
  currentOrigin: string,
  fallbackUrl: string
): string {
  const navigation = resolveNotificationNavigation(link, currentOrigin);
  if (
    navigation.kind === NotificationNavigationKindEnum.Internal ||
    navigation.kind === NotificationNavigationKindEnum.External
  ) {
    return navigation.url;
  }

  return fallbackUrl;
}

export function filterNotifications(
  notifications: readonly Notification[],
  filterMode: NotificationFilterMode
): Notification[] {
  switch (filterMode) {
    case NotificationFilterModeEnum.Messages:
      return notifications.filter(isMessageNotification);
    case NotificationFilterModeEnum.Unread:
      return notifications.filter(notification => !notification.entry.read);
    default:
      return [...notifications];
  }
}

export function groupNotifications(
  notifications: readonly Notification[],
  now: Date = new Date()
): NotificationGroup[] {
  const groups = new Map<string, Notification[]>();

  notifications.forEach(notification => {
    const label = notificationGroupLabel(notification.entry.time, now);
    const existingGroup = groups.get(label);
    if (existingGroup) {
      existingGroup.push(notification);
    } else {
      groups.set(label, [notification]);
    }
  });

  return Array.from(groups, ([label, groupedNotifications]) => ({
    label,
    notifications: groupedNotifications,
  }));
}

export function notificationGroupLabel(time: string | Date | undefined, now: Date = new Date()): string {
  const date = toValidDate(time);
  if (!date) {
    return 'Eerder';
  }

  const calendarDayDifference = differenceInCalendarDays(now, date);
  if (calendarDayDifference <= 0) {
    return 'Vandaag';
  }
  if (calendarDayDifference === 1) {
    return 'Gisteren';
  }
  if (calendarDayDifference < 7) {
    return `${calendarDayDifference} dagen geleden`;
  }
  if (calendarDayDifference < 14) {
    return '1 week geleden';
  }
  if (calendarDayDifference < 21) {
    return '2 weken geleden';
  }

  return new Intl.DateTimeFormat('nl-NL', {
    day: 'numeric',
    month: 'long',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  }).format(date);
}

export function formatNotificationDate(time: string | Date | undefined): string {
  const date = toValidDate(time);
  if (!date) {
    return '';
  }

  return new Intl.DateTimeFormat('nl-NL', {
    day: 'numeric',
    month: 'long',
  }).format(date);
}

export function formatNotificationTime(time: string | Date | undefined, now: Date = new Date()): string {
  const date = toValidDate(time);
  if (!date) {
    return '';
  }

  const minutesAgo = Math.max(0, Math.floor((now.getTime() - date.getTime()) / (60 * 1000)));
  if (minutesAgo < 60) {
    if (minutesAgo === 0) {
      return 'zojuist';
    }

    return new Intl.RelativeTimeFormat('nl-NL', { numeric: 'always' }).format(-minutesAgo, 'minute');
  }

  return new Intl.DateTimeFormat('nl-NL', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function differenceInCalendarDays(laterDate: Date, earlierDate: Date): number {
  const laterDay = new Date(laterDate.getFullYear(), laterDate.getMonth(), laterDate.getDate()).getTime();
  const earlierDay = new Date(earlierDate.getFullYear(), earlierDate.getMonth(), earlierDate.getDate()).getTime();
  return Math.round((laterDay - earlierDay) / MILLISECONDS_PER_DAY);
}

function toValidDate(time: string | Date | undefined): Date | null {
  if (!time) {
    return null;
  }

  const date = time instanceof Date ? time : new Date(time);
  return Number.isNaN(date.getTime()) ? null : date;
}
